// Diagnose Mode: Standalone (Mode 1), VDR-invoked (Mode 2), and VDR multi-session (Mode 3). Shared workflow store.
import { createReducer, on } from '@ngrx/store';
import {
  Challenge,
  PairChallenge,
  SubChallenge,
} from '../../../common/models/master-data/challenge';
import { DataEntryCategory } from '../../../common/models/master-data/data-entry';
import { Reference } from '../../../common/models/master-data/metadata';
import { DiagnosisDataEntry } from '../../../common/models/workflow/diagnosis-entry';
import { DiagnosisRequiredAction } from '../../../common/models/workflow/diagnosis-metadata';
import { DiagnosisSolutionCompatibility } from '../../../common/models/workflow/diagnosis-solution';
import { VDRJob } from '../../../common/models/workflow/vdr-job';
import {
  AdditionalAPIActions,
  CustomerSpecificationAPIActions,
  filterSubChallenge,
  initializeSession,
  initializeSessionFailure,
  initializeSessionSuccess,
  LoadChallengesFailure,
  LoadChallengesSuccess,
  loadDataByDiagnosisId,
  loadPairChallengesFailure,
  loadSeletedChallengesFailure,
  loadSeletedChallengesSuccess,
  SessionActions,
  SessionAPIActions,
  setPairChallenges,
  setSelectedChallengeLoadingState,
  SolutionAPIActions,
  updatePairChallenge,
  VDRJobAPIActions,
} from '../actions/workflow.actions';

export interface WorkflowState {
  diagnosisId: string;
  externalCode?: string;
  challenges: Challenge[];
  challengeId: string;
  pairChallenges?: PairChallenge[];
  subChallenges: SubChallenge[];
  status: string;
  isBlocking: boolean;
  modifiedAt?: Date;
  modifiedBy: string;

  isVDRSession: boolean;
  vdrJob?: VDRJob;

  isSolutionLoading: boolean;
  isDataEntryLoading: boolean;

  selectedSolutionIds: string[];
  availableSolutions?: DiagnosisSolutionCompatibility[];
  enteredDataEntries?: DiagnosisDataEntry[];
  suggestedDataEntries: DataEntryCategory[];
  requiredActions?: DiagnosisRequiredAction[];
  solutionReferences?: Reference[];

  error?: unknown; // Add error property
}

const initialtState: WorkflowState = {
  diagnosisId: '',
  challenges: [],
  challengeId: '',
  subChallenges: [],
  status: '',
  isVDRSession: false,
  isBlocking: false,
  modifiedAt: undefined,
  modifiedBy: '',
  vdrJob: undefined,

  isSolutionLoading: false,
  isDataEntryLoading: false,

  selectedSolutionIds: [],
  suggestedDataEntries: [],
  availableSolutions: [],

  error: null,
};

export const workflowReducer = createReducer(
  initialtState,
  on(loadDataByDiagnosisId, (state, { diagnosisId }) => {
    // Reset session-scoped data synchronously so switching sessions doesn't briefly flash the
    // PREVIOUS session's challenges/solutions/etc. before this session's fresh data arrives.
    if (state.diagnosisId === diagnosisId) return { ...state, diagnosisId };
    return {
      ...state,
      diagnosisId,
      pairChallenges: undefined,
      selectedSolutionIds: [],
      availableSolutions: undefined,
      enteredDataEntries: undefined,
      suggestedDataEntries: [],
      requiredActions: undefined,
      solutionReferences: undefined,
    };
  }),
  on(LoadChallengesSuccess, (state, { challenges }) => ({
    ...state,
    challenges,
  })),
  on(LoadChallengesFailure, (state, { error }) => {
    return { ...state, challenges: [], error }; // Also clear challenges on failure or handle as needed
  }),
  on(loadSeletedChallengesSuccess, (state, { diagnosisChallenges }) => ({
    ...state,
    pairChallenges: diagnosisChallenges.map((challenge) => ({
      challengeName: challenge.subChallenge.challengeName,
      subChallengeId: challenge.subChallengeId,
      subChallengeName: challenge.subChallenge.name,
    })),
  })),
  on(loadSeletedChallengesFailure, (state, { error }) => {
    return { ...state, challenges: [], error };
  }),
  on(setPairChallenges, (state, { subChallengeId }) => ({
    ...state,
    isDataEntryLoading: false,
    pairChallenges: [
      ...(state.pairChallenges || []),
      {
        challengeName:
          state.challenges.find(
            (challenge) => challenge.id === state.challengeId,
          )?.name || '',
        subChallengeId,
        subChallengeName:
          state.subChallenges.find(
            (subChallenge) => subChallenge.id === subChallengeId,
          )?.name || '',
      },
    ],
    subChallenges: state.subChallenges.map((subChallenge) => ({
      ...subChallenge,
      isSelected:
        subChallenge.id === subChallengeId ? true : subChallenge.isSelected,
    })),
  })),
  on(updatePairChallenge, (state, { diagnosisId, subChallengeId }) => ({
    ...state,
    isDataEntryLoading: false,
    pairChallenges:
      state.pairChallenges?.filter(
        (pair) => pair.subChallengeId !== subChallengeId,
      ) || [],
    subChallenges: state.subChallenges.map((subChallenge) => ({
      ...subChallenge,
      isSelected:
        subChallenge.id === subChallengeId ? false : subChallenge.isSelected,
    })),
    diagnosisId,
  })),
  on(setSelectedChallengeLoadingState, (state) => {
    return {
      ...state,
      pairChallenges: undefined,
    };
  }),
  on(loadPairChallengesFailure, (state, { modifiedInfo }) => {
    const information = modifiedInfo.split('|');
    return {
      ...state,
      isBlocking: true,
      modifiedAt: new Date(information[1]),
      modifiedBy: information[0],
      error: `This session has been blocked by [${information[0]}]`,
    };
  }),
  on(filterSubChallenge, (state, { challengeId }) => {
    const subChallenges =
      state.challenges.find((challenge) => challenge.id === challengeId)
        ?.subChallenges || [];

    return {
      ...state,
      subChallenges: subChallenges.map((subChallenge) => ({
        ...subChallenge,
        isSelected:
          !!state.pairChallenges &&
          !!state.pairChallenges.find(
            (pair) => pair.subChallengeId === subChallenge.id,
          ),
      })),
      challengeId,
    };
  }),
  on(initializeSession, (state, { initialSession }) => ({
    ...state,
    pairChallenges: [
      ...(state.pairChallenges || []),
      {
        challengeName:
          state.challenges.find(
            (challenge) => challenge.id === state.challengeId,
          )?.name || '',
        subChallengeId: initialSession.subChallengeId,
        subChallengeName:
          state.subChallenges.find(
            (subChallenge) => subChallenge.id === initialSession.subChallengeId,
          )?.name || '',
      },
    ],
    subChallenges: state.subChallenges.map((subChallenge) => ({
      ...subChallenge,
      isSelected:
        subChallenge.id === initialSession.subChallengeId
          ? true
          : subChallenge.isSelected,
    })),
  })),
  on(initializeSessionSuccess, (state, { diagnosisSession }) => ({
    ...state,
    diagnosisId: diagnosisSession.id,
  })),
  on(initializeSessionFailure, (state, { error }) => {
    return { ...state, diagnosisId: '', error };
  }),

  on(
    SessionActions.startNewWorkflow,
    SessionAPIActions.cancelSessionSuccess,
    (state) => ({
      ...state,
      diagnosisId: '',
      status: '',
      isBlocking: false,
      challenges: state.challenges,
      subChallenges: [],
      pairChallenges: [],
      enteredDataEntries: [],
      suggestedDataEntries: [],
      selectedSolutionIds: [],
      availableSolutions: [],
      isDataEntryLoading: false,
      isSolutionLoading: false,
    }),
  ),

  on(SessionAPIActions.completeOneOffSessionSuccess, (state, { session }) => ({
    ...state,
    status: session.diagnosisStatusName,
  })),

  on(SessionAPIActions.completeVDRSessionSuccess, (state, { session }) => ({
    ...state,
    status: session.diagnosisStatusName,
  })),

  on(
    SessionAPIActions.completeDiagnosisModeThreeSuccess,
    (state, { session }) => ({
      ...state,
      status: session.diagnosisStatusName,
    }),
  ),

  on(SessionAPIActions.reopenSessionSuccess, (state, { session }) => ({
    ...state,
    solutionReferences: [],
    requiredActions: [],
    status: session.diagnosisStatusName,
  })),

  on(SessionAPIActions.resetSessionSuccess, (state) => {
    return {
      ...state,
      challengeId: '',
      pairChallenges: [],
      enteredDataEntries: [],
      suggestedDataEntries: [],
      selectedSolutionIds: [],
      availableSolutions: [],
      isDataEntryLoading: false,
      isSolutionLoading: false,
    };
  }),

  on(SessionAPIActions.loadSessionInformationSuccess, (state, { session }) => {
    return {
      ...state,
      externalCode: session.externalId,
      status: session.diagnosisStatusName,
      isBlocking: session.isBlocking,
      modifiedAt: session.modifiedAt,
      modifiedBy: session.modifiedBy,
    };
  }),

  // To get Selected Solutions of a session
  on(
    SolutionAPIActions.chooseSolutionSuccess,
    (state, { solutions, selectedSolutionId }) => {
      const values = {
        ...state,
        availableSolutions: solutions,
        isSolutionLoading: false,
        selectedSolutionIds: [
          ...(state.selectedSolutionIds || []),
          selectedSolutionId,
        ],
      };

      return values;
    },
  ),
  on(
    SolutionAPIActions.removeSolutionSuccess,
    (state, { solutions, selectedSolutionId }) => ({
      ...state,
      availableSolutions: solutions,
      isSolutionLoading: false,
      selectedSolutionIds:
        state.selectedSolutionIds?.filter(
          (solutionId) => solutionId !== selectedSolutionId,
        ) || [],
    }),
  ),
  on(SolutionAPIActions.removeAllSolutionsSuccess, (state, { solutions }) => ({
    ...state,
    availableSolutions: solutions,
    isSolutionLoading: false,
    selectedSolutionIds: [],
  })),
  on(
    SolutionAPIActions.getAvailableSolutionsSuccess,
    (state, { solutions }) => ({
      ...state,
      availableSolutions: solutions,
      isSolutionLoading: false,
    }),
  ),
  on(
    SolutionAPIActions.getSelectedSolutionsSuccess,
    (state, { solutionIds }) => ({
      ...state,
      selectedSolutionIds: solutionIds,
      isSolutionLoading: false,
    }),
  ),
  on(
    SolutionAPIActions.getSolutionCompatibilitiesSuccess,
    (state, { solutions }) => ({
      ...state,
      availableSolutions: solutions,
      isSolutionLoading: false,
    }),
  ),
  on(SolutionAPIActions.setSolutionLoadingState, (state) => {
    return {
      ...state,
      isSolutionLoading: true,
    };
  }),
  on(
    SolutionAPIActions.chooseSolutionFailure,
    SolutionAPIActions.removeSolutionFailure,
    SolutionAPIActions.removeAllSolutionsFailure,
    SessionAPIActions.reopenSessionFailure,
    SessionAPIActions.completeVDRSessionFailure,
    SessionAPIActions.completeOneOffSessionFailure,
    (state, { modifiedInfo }) => {
      const information = modifiedInfo.split('|');
      return {
        ...state,
        isBlocking: true,
        modifiedAt: new Date(information[1] || ''),
        modifiedBy: information[0] || '',
        isSolutionLoading: false,
        error: `This session has been blocked by [${information[0] || ''}]`,
      };
    },
  ),

  on(SolutionAPIActions.requestFailure, (state, { error }) => {
    return {
      ...state,
      availableSolutions: [],
      error,
    };
  }),

  // To get related Data Entries based on selected sub-challenges of a session
  on(
    CustomerSpecificationAPIActions.upsertEntryValueSuccess,
    (state, { solutions }) => ({
      ...state,
      selectedSolutionIds: [],
      availableSolutions: solutions,
      isSolutionLoading: false,
    }),
  ),
  on(
    CustomerSpecificationAPIActions.removeEntryValueSuccess,
    (state, { solutions }) => ({
      ...state,
      selectedSolutionIds: [],
      availableSolutions: solutions,
      isSolutionLoading: false,
    }),
  ),
  on(
    CustomerSpecificationAPIActions.removeAllEntryValuesSuccess,
    (state, { solutions }) => ({
      ...state,
      selectedSolutionIds: [],
      availableSolutions: solutions,
      isSolutionLoading: false,
    }),
  ),
  on(
    CustomerSpecificationAPIActions.upsertEntryValueFailure,
    CustomerSpecificationAPIActions.removeEntryValueFailure,
    CustomerSpecificationAPIActions.removeAllEntryValuesFailure,
    (state, { modifiedInfo }) => {
      const information = modifiedInfo.split('|');
      return {
        ...state,
        isBlocking: true,
        modifiedAt: new Date(information[1]),
        modifiedBy: information[0],
        error: `This session has been blocked by [${information[0]}]`,
      };
    },
  ),
  on(
    CustomerSpecificationAPIActions.getEnteredDataEntriesSuccess,
    (state, { enteredValues }) => ({
      ...state,
      enteredDataEntries: enteredValues,
      isDataEntryLoading: false,
    }),
  ),

  // To Load Data Entries from API
  on(
    CustomerSpecificationAPIActions.loadSuggestedDataEntriesSuccess,
    (state, { dataEntries }) => ({
      ...state,
      suggestedDataEntries: dataEntries,
    }),
  ),
  on(CustomerSpecificationAPIActions.setDataEntryLoadingState, (state) => {
    return { ...state, isDataEntryLoading: true };
  }),
  on(CustomerSpecificationAPIActions.requestFailure, (state, { error }) => {
    return {
      ...state,
      isDataEntryLoading: false,
      error,
    };
  }),

  // To get related VDR Job based of a session
  on(VDRJobAPIActions.loadVDRJobInformationSuccess, (state, { vdrJob }) => {
    return {
      ...state,
      vdrJob,
      isVDRSession: true,
      error: null,
    };
  }),

  on(VDRJobAPIActions.requestFailure, (state, { error }) => {
    return { ...state, vdrJob: undefined, isVDRSession: false, error };
  }),

  // To Load Additional Information from API
  on(
    AdditionalAPIActions.loadSolutionReferencesSuccess,
    (state, { references }) => ({
      ...state,
      solutionReferences: references,
    }),
  ),
  on(
    AdditionalAPIActions.loadRequiredActionsSuccess,
    (state, { requiredActions }) => ({
      ...state,
      requiredActions,
    }),
  ),
  on(AdditionalAPIActions.setRequiredActionLoadingState, (state) => {
    return { ...state, requiredActions: undefined };
  }),
  on(AdditionalAPIActions.setSolutionReferenceLoadingState, (state) => {
    return { ...state, solutionReferences: undefined };
  }),
  on(AdditionalAPIActions.requestFailure, (state, { error }) => {
    return {
      ...state,
      error,
    };
  }),
);
